import { useCallback, useEffect, useRef } from 'react';

type Options = {
  blockTimeMs?: number; // tiempo mínimo entre clicks
  allowIfPromisePending?: boolean; // default false: bloquea mientras la promesa esté pendiente
};

const usePreventMultipleClicks = <TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult> | TResult,
  options?: Options
) => {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const lockedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);

  const blockTimeMs = options?.blockTimeMs ?? 800;
  const allowIfPromisePending = options?.allowIfPromisePending ?? false;

  // cleanup por si el componente se desmonta
  useEffect(
    () => () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    },
    []
  );

  const unlockWithDelay = useCallback(() => {
    if (timeoutRef.current != null) window.clearTimeout(timeoutRef.current);

    timeoutRef.current = window.setTimeout(() => {
      lockedRef.current = false;
      timeoutRef.current = null;
    }, blockTimeMs);
  }, [blockTimeMs]);

  const wrapped = useCallback(
    async (...args: TArgs) => {
      if (lockedRef.current) return undefined as unknown as TResult;

      lockedRef.current = true;

      try {
        const result = await fnRef.current(...args);

        if (!allowIfPromisePending) unlockWithDelay();
        else lockedRef.current = false;

        return result;
      } catch (err) {
        if (!allowIfPromisePending) unlockWithDelay();
        else lockedRef.current = false;

        throw err;
      }
    },
    [allowIfPromisePending, unlockWithDelay]
  );

  return wrapped;
};

export default usePreventMultipleClicks;
